/**
 * Подтверждение адреса по ссылке из письма.
 *
 * Приложение может быть открыто по ссылке в любой момент — в том числе в
 * браузере, где локального пространства ещё нет. Подтверждение адреса от
 * пространства не зависит; от него зависит только перенос прогресса.
 */

import { useEffect, useRef, useState } from 'react';
import {
  RegistrationError,
  attachWorkspaceToUser,
  verifyEmailAndClaim,
} from '@type2x/feature-accounts';
import type { Workspace } from '@type2x/feature-personal-workspace';

import type { Runtime } from '../runtime.js';
import type { AppScreen } from '../state/app-state.js';
import type { VerificationState } from '../state/verification.js';

export interface VerificationDeps {
  readonly workspace: Workspace | null;
  readonly loading: boolean;
  readonly commit: (next: Workspace) => Promise<void>;
  readonly flush: () => Promise<unknown>;
  readonly setScreen: (screen: AppScreen) => void;
}

/** Токен подтверждения адреса — только из ссылки подтверждения, не из любой. */
function verificationToken(runtime: Runtime): string | null {
  const link = runtime.emailLink;
  return link !== null && link.kind === 'verify-email' ? link.token : null;
}

/** Подтверждает адрес, если приложение открыто по ссылке из письма. */
export function useVerificationLink(runtime: Runtime, deps: VerificationDeps): VerificationState {
  const { workspace, loading, commit, flush, setScreen } = deps;

  // Начальное состояние известно сразу: ссылка либо была, либо нет.
  const [state, setState] = useState<VerificationState>(
    verificationToken(runtime) === null ? { status: 'idle' } : { status: 'checking' },
  );
  const started = useRef(false);

  useEffect(() => {
    const token = verificationToken(runtime);
    // Загрузку пространства ждём не ради подтверждения, а ради переноса:
    // пока неизвестно, есть ли прогресс, переносить нечего и рано.
    if (token === null || loading || started.current) return;
    started.current = true;
    setScreen({ name: 'account' });

    void (async () => {
      try {
        const outcome = await verifyEmailAndClaim(
          runtime.accounts,
          token,
          // Переносить нечего, если пространства нет: по ссылке могли перейти
          // и в другом браузере, где прогресс не накапливался.
          workspace === null
            ? null
            : { installationId: runtime.installationId, syncId: runtime.ids.newSyncId() },
        );

        if (workspace !== null && outcome.progress !== 'taken') {
          // Прогресс не пересоздаётся: меняется только владелец.
          await commit(attachWorkspaceToUser(workspace, outcome, runtime.clock.isoNow()));
          // Появился аккаунт — накопленная гостем очередь наконец уходит.
          await flush();
        }

        setState({ status: 'done', outcome });
      } catch (error) {
        setState({
          status: 'failed',
          problem: error instanceof RegistrationError ? error.problem : 'server-error',
        });
      }
    })();
  }, [runtime, workspace, loading, commit, flush, setScreen]);

  return state;
}
